/**
 * Attendance Reports Component
 * Admin → Reports section
 * 
 * Features:
 * - View attendance data with filters
 * - Export to CSV, Excel
 * - Role-based access (Admin: full, TL: team, Employee: view only)
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  FileSpreadsheet,
  FileText,
  Loader2,
  Building2,
  UserRound,
} from 'lucide-react';
import { Button } from '../ui/button';
import { AnFilterBar, AnFilterField, AnDateRange, AnResetButton, AnRunButton } from '../analytics/ui/AnalyticsUi';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import AttendanceDailyView from './AttendanceDailyView';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AttendanceReports = ({ hideHeader = false }) => {
  const [exporting, setExporting] = useState(false);
  const [canExport, setCanExport] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Filter state — ATT-DEPT-FILTER-001: Session Details / Month / Display TZ /
  // Status controls removed; Role replaced by Department (teams source of truth)
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    user_id: '',
    team_id: ''
  });
  const [filterOptions, setFilterOptions] = useState({
    users: [],
    teams: []
  });

  // Fetch filter options
  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/reports/attendance/filters`);
      setFilterOptions(response.data);
      setCanExport(response.data.can_export);
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  }, []);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
      user_id: '',
      team_id: ''
    });
  };

  // ATTENDANCE-REGISTER-001 — month derived from the From Date filter
  // (falls back to the current month); display timezone is employee-local.
  const registerMonth = (filters.start_date || new Date().toISOString().slice(0, 10)).slice(0, 7);
  const handleRegisterExport = async () => {
    if (!canExport) {
      toast.error('Export not permitted for your role');
      return;
    }
    try {
      setExporting(true);
      const params = new URLSearchParams({ month: registerMonth });
      if (filters.team_id) params.append('team_id', filters.team_id);
      const response = await axios.get(
        `${API}/api/reports/attendance/export/attendance-register-excel?${params.toString()}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `attendance_register_${registerMonth}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Attendance Register exported');
    } catch (err) {
      console.error('Register export error:', err);
      toast.error('Failed to export Attendance Register');
    } finally {
      setExporting(false);
    }
  };

  // Payroll Summary Export handler
  const handlePayrollExport = async (format, excludeActive = false) => {
    if (!canExport) {
      toast.error('Export not permitted for your role');
      return;
    }

    try {
      setExporting(true);
      
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.team_id) params.append('team_id', filters.team_id);
      if (excludeActive) params.append('exclude_active', 'true');

      const endpoint = format === 'csv' 
        ? `/api/reports/attendance/export/payroll-csv`
        : `/api/reports/attendance/export/payroll-excel`;

      const response = await axios.get(`${API}${endpoint}?${params.toString()}`, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = format === 'csv' ? 'payroll_summary.csv' : 'payroll_summary.xlsx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      const exportType = excludeActive ? 'Payroll-ready summary' : 'Payroll summary';
      toast.success(`${exportType} exported as ${format.toUpperCase()}`);
    } catch (err) {
      console.error('Payroll export error:', err);
      toast.error(`Failed to export payroll summary as ${format.toUpperCase()}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header — hidden inside the Analytics module (ANALYTICS-HEADER-001) */}
      {!hideHeader && (
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Attendance Reports</h2>
          <p className="text-sm text-slate-500 mt-1">View and export attendance data for HR, payroll, and audit compliance</p>
        </div>
      )}

      {/* Premium compact filter bar — same From/To/team_id/user_id state + immediate-apply semantics as before */}
      <AnFilterBar testId="attendance-filter-bar" actions={
        <>
          <AnResetButton onClick={clearFilters} testId="clear-filters-btn" />
          <AnRunButton onClick={() => setRefreshKey((k) => k + 1)} testId="refresh-btn" />
        </>
      }>
        <AnDateRange start={filters.start_date} end={filters.end_date} testId="filter-date-range"
          onChange={({ start, end }) => setFilters(prev => ({ ...prev, start_date: start, end_date: end }))} />
        {/* Hidden mirrors keep the original From/To test hooks + values addressable */}
        <input type="hidden" value={filters.start_date} data-testid="filter-start-date" readOnly />
        <input type="hidden" value={filters.end_date} data-testid="filter-end-date" readOnly />
        <AnFilterField icon={Building2} label="Department" className="min-w-[200px]">
          <Select value={filters.team_id || 'all'} onValueChange={(v) => handleFilterChange('team_id', v === 'all' ? '' : v)}>
            <SelectTrigger data-testid="filter-department"><SelectValue placeholder="All Departments" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {(filterOptions.teams || []).map(team => <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </AnFilterField>
        <AnFilterField icon={UserRound} label="User" className="min-w-[200px]">
          <Select value={filters.user_id || 'all'} onValueChange={(v) => handleFilterChange('user_id', v === 'all' ? '' : v)}>
            <SelectTrigger data-testid="filter-user"><SelectValue placeholder="All Users" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Users</SelectItem>
              {filterOptions.users.map(user => <SelectItem key={user.id} value={user.id}>{user.full_name || user.email}</SelectItem>)}
            </SelectContent>
          </Select>
        </AnFilterField>
      </AnFilterBar>

      {/* Primary report actions — below filters (ATT-DEPT-FILTER-001) */}
      {canExport && (
        <div className="flex flex-wrap items-center gap-3" data-testid="report-actions">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button disabled={exporting} className="an-btn gap-2 h-10 px-4 rounded-xl text-[13px] font-semibold bg-emerald-600 hover:bg-emerald-700 shadow-[0_8px_20px_-10px_rgba(5,150,105,.7)]" data-testid="payroll-summary-btn">
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
                Payroll Summary
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
              <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 border-b border-slate-100">Daily Aggregated Summary</div>
              <DropdownMenuItem onClick={() => handlePayrollExport('csv', false)} className="gap-2 cursor-pointer"><FileText className="w-4 h-4" />Export as CSV (All)</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handlePayrollExport('excel', false)} className="gap-2 cursor-pointer"><FileSpreadsheet className="w-4 h-4" />Export as Excel (All)</DropdownMenuItem>
              <div className="px-2 py-1.5 text-xs font-semibold text-emerald-600 border-t border-slate-100 mt-1">Payroll Ready (Completed Only)</div>
              <DropdownMenuItem onClick={() => handlePayrollExport('csv', true)} className="gap-2 cursor-pointer text-emerald-700"><FileText className="w-4 h-4" />CSV - Exclude Active</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handlePayrollExport('excel', true)} className="gap-2 cursor-pointer text-emerald-700"><FileSpreadsheet className="w-4 h-4" />Excel - Exclude Active</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button disabled={exporting} onClick={handleRegisterExport} className="an-btn an-btn-primary gap-2 h-10 px-4 rounded-xl text-[13px] font-semibold" data-testid="register-export-btn">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
            Attendance Register
          </Button>
          <span className="text-xs text-slate-400">Register month: {registerMonth} (from the From Date filter)</span>
        </div>
      )}

      {/* ATT-DAILY-001 — consolidated daily view (single primary view) */}
      <AttendanceDailyView filters={filters} refreshKey={refreshKey} />
    </div>
  );
};

export default AttendanceReports;
